// ./src/components/features/Logbook/LogbookList/LogbookList.tsx
//
// Unified list component for the Logbook page.
// Renders Refuels, Expenses, and Checkpoints as a single chronological list
// using responsive cards (RecordCard) with Framer Motion entrance animations.
//
// COMPACT LAYOUT (v2):
// - Type badge is now part of RecordCard header row (no separate line)
// - Refuel content: single row for volume + price/unit + trip + consumption
// - Partial refuel shows tank % after refuel
// - Expense content: kind badge inline, short note same line if short
// - Audit info only shown when creator differs from current logged-in user
// - All rows use horizontal space aggressively

import React, { useMemo, useCallback } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Badge,
  Box,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";

import { EXPENSE_TYPES, EXPENSE_TYPE_OPTIONS } from "@/config";

import { formatNumber, formatCurrency } from "@/utils/formatHelpers";
import { detectOdometerAnomalies } from "@/utils/expenseHelpers";

import { Expense } from "@/utils/api/ExpenseService";

import {
  useEntranceAnimation,
  entranceListVariants,
  entranceCardVariants,
} from "@/hooks/useEntranceAnimation";

import { useUserProfileContext } from "@/contexts/UserProfileContext";
import { useAppEssentialsContext } from "@/contexts/AppEssentialsContext";
import { useOptionalExpensesContext } from "@/contexts/ExpensesContext";
import { useDashboardContext } from "@/contexts/DashboardContext";

import { AppIcon } from "@/components/generic/AppIcon";
import { EmptyState } from "@/components/generic/EmptyState";
import { useSwipeableList } from "@/components/generic/SwipeableCard";
import { RecordCard, RecordCardSkeleton } from "@/components/generic/RecordCard";
import { LoadMoreButton } from "@/components/generic/LoadMoreButton";

// ============================================
// Constants
// ============================================

const TANK_TYPE_ADDL = "addl";

// Fix #1: derive the full set of known type values from EXPENSE_TYPE_OPTIONS
// so the "all selected" shortcut stays correct if new types are added.
const ALL_EXPENSE_TYPE_VALUES = EXPENSE_TYPE_OPTIONS.map((opt) => opt.value);

/** Map expense type to visual config */
const TYPE_CONFIG = {
  [EXPENSE_TYPES.REFUEL]: {
    icon: "GasStation" as const,
    color: "green",
    i18nKey: "logbook:types.refuel",
  },
  [EXPENSE_TYPES.EXPENSE]: {
    icon: "Receipt" as const,
    color: "blue",
    i18nKey: "logbook:types.expense",
  },
  [EXPENSE_TYPES.CHECKPOINT]: {
    icon: "Flag" as const,
    color: "orange",
    i18nKey: "logbook:types.checkpoint",
  },
} as const;

// ============================================
// Types
// ============================================

export interface LogbookListProps {
  records?: Expense[];
  loading?: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  selectedIds?: string[];
  onSelectionChange?: (selectedIds: string[]) => void;
  onEdit?: (record: Expense) => void;
  onDuplicate?: (record: Expense) => void;
  onDelete?: (record: Expense) => void;
  onRecordClick?: (record: Expense) => void;
  deletingId?: string | null;
  highlightedId?: string | null;
  onAdd?: () => void;
  selectable?: boolean;
  activeTypes?: number[];
  // Fix #5: removed unused showTypeFilters and onActiveTypesChange props
}

// ============================================
// Loading Skeleton
// ============================================

const LoadingSkeleton: React.FC = () => {
  return (
    <Stack gap="sm">
      {Array.from({ length: 6 }).map((_, index) => (
        <RecordCardSkeleton
          key={index}
          showPrice={index % 3 !== 2}
          customContentRows={index % 3 === 0 ? 2 : 1}
        />
      ))}
    </Stack>
  );
};

// ============================================
// Refuel-Specific Content — Compact
// ============================================

interface RefuelContentProps {
  record: Expense;
  volumeLabel: string;
  distanceLabel: string;
  consumptionLabel: string;
  currency: string;
}

const RefuelContentComponent: React.FC<RefuelContentProps> = ({
  record,
  volumeLabel,
  distanceLabel,
  consumptionLabel,
  currency,
}) => {
  const { t } = useTranslation(["refuels"]);
  const isAdditionalTank = record.tankType === TANK_TYPE_ADDL;
  const isPartialRefuel = !record.isFullTank;

  // For partial refuels, show fuelInTank as percentage (value is 0..1, display as 0..100)
  const tankPercentAfter = isPartialRefuel && record.fuelInTank != null
    ? Math.round(record.fuelInTank * 100)
    : null;

  return (
    <>
      {/* Row 1: Volume · Full/Partial badge · Grade · Price/unit */}
      <Group gap="sm" wrap="wrap" align="center">
        {/* Volume */}
        <Group gap={4}>
          <AppIcon iconName="GasStation" size={16} color="green" />
          <Text size="md" fw={500}>
            {formatNumber(record.refuelVolumeDisplay, 2)} {volumeLabel}
          </Text>
        </Group>

        {/* Full tank / Partial + tank % */}
        {record.isFullTank ? (
          <Badge variant="light" size="sm" color="green">
            {t("refuels:list.fullTank")}
          </Badge>
        ) : (
          tankPercentAfter != null && (
            <Badge variant="light" size="sm" color="yellow">
              {t("refuels:list.tankAfter", {
                pct: tankPercentAfter,
                defaultValue: "Tank {{pct}}%",
              })}
            </Badge>
          )
        )}

        {/* Additional tank badge */}
        {isAdditionalTank && (
          <Badge variant="light" size="sm" color="blue">
            {t("refuels:list.additionalTank", { defaultValue: "Addl Tank" })}
          </Badge>
        )}

        {/* Fuel grade */}
        {record.fuelGrade && (
          <Badge variant="outline" size="sm" color="gray">
            {record.fuelGrade}
          </Badge>
        )}

        {/* Price per volume */}
        {record.pricePerVolume != null && (
          <Text size="md" c="dimmed">
            {/* Fix #9: use ?? instead of || for null/undefined fallback */}
            @ {formatCurrency(
              record.pricePerVolume,
              (record.totalPriceInHc ? record.homeCurrency : record.paidInCurrency) ?? currency
            )}/{volumeLabel}
          </Text>
        )}

        {/* Desktop: trip + consumption inline with volume row */}
        {record.tripMeter != null && record.tripMeter > 0 && (
          <Box visibleFrom="sm">
            <Group gap={4}>
              <AppIcon iconName="Route" size={16} color="gray" />
              <Text size="md" c="dimmed">
                {formatNumber(record.tripMeter, 0)} {distanceLabel}
              </Text>
            </Group>
          </Box>
        )}
        {record.consumption != null && record.consumption > 0 && (
          <Box visibleFrom="sm">
            <Group gap={4}>
              <AppIcon iconName="ChartLine" size={16} color="gray" />
              <Text size="md" c="dimmed">
                {formatNumber(record.consumption, 1)} {consumptionLabel}
              </Text>
            </Group>
          </Box>
        )}
      </Group>

      {/* Mobile: trip + consumption on their own row */}
      {(record.tripMeter != null && record.tripMeter > 0 ||
        record.consumption != null && record.consumption > 0) && (
          <Box hiddenFrom="sm">
            <Group gap="md" wrap="wrap" align="center">
              {record.tripMeter != null && record.tripMeter > 0 && (
                <Group gap={4}>
                  <AppIcon iconName="Route" size={16} color="gray" />
                  <Text size="md" c="dimmed">
                    {formatNumber(record.tripMeter, 0)} {distanceLabel}
                  </Text>
                </Group>
              )}
              {record.consumption != null && record.consumption > 0 && (
                <Group gap={4}>
                  <AppIcon iconName="ChartLine" size={16} color="gray" />
                  <Text size="md" c="dimmed">
                    {formatNumber(record.consumption, 1)} {consumptionLabel}
                  </Text>
                </Group>
              )}
            </Group>
          </Box>
        )}
    </>
  );
};

const RefuelContent = React.memo(RefuelContentComponent);

// ============================================
// Expense-Specific Content — Compact
// ============================================

interface ExpenseContentProps {
  record: Expense;
  getExpenseKindName: (id: number | undefined) => string;
}

const ExpenseContentComponent: React.FC<ExpenseContentProps> = ({
  record,
  getExpenseKindName,
}) => {
  const kindName = getExpenseKindName(record.kindId);

  return (
    <>
      {/* Kind badge + short note — on one row */}
      <Group gap="sm" wrap="wrap" align="center">
        {kindName && (
          <Badge variant="light" size="sm" color="blue">
            {kindName}
          </Badge>
        )}
        {record.shortNote && (
          <Text size="md">{record.shortNote}</Text>
        )}
      </Group>
    </>
  );
};

const ExpenseContent = React.memo(ExpenseContentComponent);

// ============================================
// Checkpoint-Specific Content — Compact
// ============================================

interface CheckpointContentProps {
  record: Expense;
}

const CheckpointContentComponent: React.FC<CheckpointContentProps> = ({
  record,
}) => {
  return (
    <>
      {/* Comments inline */}
      {record.comments && <Text size="md">{record.comments}</Text>}
    </>
  );
};

const CheckpointContent = React.memo(CheckpointContentComponent);

// ============================================
// Single Logbook Card — delegates to RecordCard
// ============================================

interface LogbookCardProps {
  record: Expense;
  selected: boolean;
  onSelect: (id: string, checked: boolean) => void;
  onClick?: (record: Expense) => void;
  onEdit?: (record: Expense) => void;
  onDuplicate?: (record: Expense) => void;
  onDelete?: (record: Expense) => void;
  selectable: boolean;
  highlighted: boolean;
  isDeleting: boolean;
  currency: string;
  odometerUnit: string;
  volumeLabel: string;
  distanceLabel: string;
  consumptionLabel: string;
  hasOdometerAnomaly: boolean;
  getExpenseKindName: (id: number | undefined) => string;
  // Swipe management props
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const LogbookCardComponent: React.FC<LogbookCardProps> = ({
  record,
  selected,
  onSelect,
  onClick,
  onEdit,
  onDuplicate,
  onDelete,
  selectable,
  highlighted,
  isDeleting,
  currency,
  odometerUnit,
  volumeLabel,
  distanceLabel,
  consumptionLabel,
  hasOdometerAnomaly,
  getExpenseKindName,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const { t } = useTranslation(["logbook"]);

  // Determine price to show
  const displayPrice = useMemo(() => {
    if (record.expenseType === EXPENSE_TYPES.CHECKPOINT) {
      return null;
    }

    return record.totalPriceInHc ?? record.totalPrice;
  }, [record]);

  // Price color per type
  const priceColor = record.expenseType === EXPENSE_TYPES.REFUEL ? "green.7" : "blue.7";

  // Car label
  const carLabel = record.car?.label;

  // Type config for header badge
  const typeConfig = record.expenseType
    ? TYPE_CONFIG[record.expenseType as keyof typeof TYPE_CONFIG]
    : null;

  return (
    <RecordCard
      record={record}
      recordId={record.id}
      // Type indicator — passed to header row
      typeIcon={typeConfig?.icon}
      typeColor={typeConfig?.color}
      typeLabel={typeConfig ? t(typeConfig.i18nKey) : undefined}
      // Header data
      whenDone={record.whenDone}
      carLabel={carLabel}
      odometer={record.odometer}
      distanceLabel={odometerUnit}
      hasOdometerAnomaly={hasOdometerAnomaly}
      // Footer data
      whereDone={record.whereDone}
      location={record.location}
      weatherData={record}
      tags={record.tags}
      // Price
      totalPrice={displayPrice}
      priceCurrency={record.totalPriceInHc ? record.homeCurrency : record.paidInCurrency}
      fallbackCurrency={currency}
      priceColor={priceColor}
      // State
      selected={selected}
      onSelect={onSelect}
      selectable={selectable}
      highlighted={highlighted}
      isDeleting={isDeleting}
      // Actions
      onClick={onClick}
      onEdit={onEdit}
      onDuplicate={onDuplicate}
      onDelete={onDelete}
      // Swipe
      forceClose={forceClose}
      onForceCloseComplete={onForceCloseComplete}
      onSwipeStateChange={onSwipeStateChange}
    >
      {/* Type-specific content — compact versions */}
      {record.expenseType === EXPENSE_TYPES.REFUEL && (
        <RefuelContent
          record={record}
          volumeLabel={volumeLabel}
          distanceLabel={distanceLabel}
          consumptionLabel={consumptionLabel}
          currency={currency}
        />
      )}
      {record.expenseType === EXPENSE_TYPES.EXPENSE && (
        <ExpenseContent
          record={record}
          getExpenseKindName={getExpenseKindName}
        />
      )}
      {record.expenseType === EXPENSE_TYPES.CHECKPOINT && (
        <CheckpointContent
          record={record}
        />
      )}
    </RecordCard>
  );
};

const LogbookCard = React.memo(LogbookCardComponent);

// ============================================
// Main Component
// ============================================

const LogbookListComponent: React.FC<LogbookListProps> = ({
  records: recordsProp,
  loading: loadingProp,
  loadingMore: loadingMoreProp,
  hasMore: hasMoreProp,
  onLoadMore: onLoadMoreProp,
  selectedIds = [],
  onSelectionChange,
  onEdit,
  onDuplicate,
  onDelete,
  onRecordClick,
  deletingId: deletingIdProp,
  highlightedId,
  onAdd,
  selectable = true,
  activeTypes = [EXPENSE_TYPES.REFUEL, EXPENSE_TYPES.EXPENSE, EXPENSE_TYPES.CHECKPOINT],
}) => {
  const { t } = useTranslation(["logbook", "common"]);
  const {
    currency,
    volumeLabelShort,
    distanceLabelShort,
    consumptionLabelShort,
  } = useUserProfileContext();
  const { getExpenseKindNameById } = useAppEssentialsContext();
  const { getCarById } = useDashboardContext();

  // ── Context vs Props resolution ──
  const expensesContext = useOptionalExpensesContext();

  const records = recordsProp ?? expensesContext?.expenses ?? [];
  const loading = loadingProp ?? expensesContext?.loading ?? false;
  const loadingMore = loadingMoreProp ?? expensesContext?.loadingMore ?? false;
  const hasMore = hasMoreProp ?? expensesContext?.hasMore ?? false;
  const onLoadMore = onLoadMoreProp ?? expensesContext?.loadMore;
  const deletingId = deletingIdProp ?? expensesContext?.removingId ?? null;

  // Manage swipeable cards
  const {
    openCard,
    closeAllCards,
    shouldForceClose,
    handleForceCloseComplete,
  } = useSwipeableList();

  // Entrance animation
  const { hasAnimated } = useEntranceAnimation({ itemCount: records.length });

  // ── Filtered records ──

  // Fix #1: compare against the full known set of type values, not a hardcoded length.
  // Fix #2: guard against null/undefined expenseType before calling includes().
  const filteredRecords = useMemo(() => {
    if (activeTypes.length === ALL_EXPENSE_TYPE_VALUES.length) return records;
    return records.filter(
      (r) => r.expenseType != null && activeTypes.includes(r.expenseType)
    );
  }, [records, activeTypes]);

  // ── Odometer anomalies ──

  const odometerAnomalies = useMemo(
    () => detectOdometerAnomalies(filteredRecords),
    [filteredRecords]
  );

  // ── Odometer unit map ──

  // Fix #7: pre-compute translated unit strings per carId instead of calling
  // getCarById on every render of every card via a passed-down callback.
  // Fix #8: resolve the full translated i18n key here at the list level so that
  // static analysis tools (i18next-parser) can see the concrete key strings.
  // Fix #9: use ?? instead of || for the fallback (mileageIn could be "").
  const odometerUnitMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const record of filteredRecords) {
      if (!record.carId || map.has(record.carId)) continue;
      const car = getCarById(record.carId);
      const unitKey = car?.mileageIn ?? distanceLabelShort;
      map.set(record.carId, t(`common:${unitKey}`));
    }
    return map;
  }, [filteredRecords, getCarById, distanceLabelShort, t]);

  // ── Selection ──

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const handleSelect = useCallback(
    (id: string, checked: boolean) => {
      if (!onSelectionChange) return;
      const newSelection = checked
        ? [...selectedIds, id]
        : selectedIds.filter((sid) => sid !== id);
      onSelectionChange(newSelection);
    },
    [selectedIds, onSelectionChange]
  );

  // ── Row / card click ──

  const handleRecordClick = useCallback(
    (record: Expense) => {
      closeAllCards();
      onRecordClick?.(record);
    },
    [onRecordClick, closeAllCards]
  );

  const handleEdit = useCallback(
    (record: Expense) => {
      closeAllCards();
      onEdit?.(record);
    },
    [onEdit, closeAllCards]
  );

  const handleDuplicate = useCallback(
    (record: Expense) => {
      closeAllCards();
      onDuplicate?.(record);
    },
    [onDuplicate, closeAllCards]
  );

  const handleDelete = useCallback(
    (record: Expense) => {
      closeAllCards();
      onDelete?.(record);
    },
    [onDelete, closeAllCards]
  );

  // ── Swipe state ──

  const handleSwipeStateChange = useCallback(
    (recordId: string, isOpen: boolean) => {
      if (isOpen) {
        openCard(recordId);
      }
    },
    [openCard]
  );

  // ── Loading state ──

  if (loading && records.length === 0) {
    return <LoadingSkeleton />;
  }

  // ── Empty state ──

  if (!loading && records.length === 0) {
    return (
      <Paper withBorder p="xl">
        <EmptyState
          title={t("logbook:emptyState.title")}
          description={t("logbook:emptyState.description")}
          iconName="BookOpen"
          actionLabel={t("logbook:emptyState.actionLabel")}
          onAction={onAdd}
          size="md"
        />
      </Paper>
    );
  }

  // Fix #3: gate filtersHideAll on !loading to prevent the filtered-empty state
  // from appearing while a load-more fetch is in flight.
  const filtersHideAll = !loading && filteredRecords.length === 0 && records.length > 0;

  return (
    <Stack gap="sm">
      {/* Filtered-empty state */}
      {filtersHideAll && (
        <Paper withBorder p="xl">
          <EmptyState
            title={t("logbook:emptyState.filteredTitle")}
            description={t("logbook:emptyState.filteredDescription")}
            iconName="Filter"
            size="sm"
          />
        </Paper>
      )}

      {/* Card list with animations */}
      {!filtersHideAll && (
        <>
          <motion.div
            variants={hasAnimated ? undefined : entranceListVariants}
            initial={hasAnimated ? false : "hidden"}
            animate={hasAnimated ? undefined : "visible"}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--mantine-spacing-sm)",
            }}
          >
            <AnimatePresence
              // Fix #10: use popLayout so the exiting item collapses its space
              // while the entering items animate in, giving a smoother delete UX.
              mode="popLayout"
            >
              {filteredRecords.map((record) => (
                <motion.div
                  key={record.id}
                  variants={hasAnimated ? undefined : entranceCardVariants}
                  initial={hasAnimated ? false : "hidden"}
                  animate={hasAnimated ? undefined : "visible"}
                  exit="exit"
                  layout
                >
                  <LogbookCard
                    record={record}
                    selected={selectedSet.has(record.id ?? "")}
                    onSelect={handleSelect}
                    onClick={handleRecordClick}
                    onEdit={onEdit ? handleEdit : undefined}
                    onDuplicate={onDuplicate ? handleDuplicate : undefined}
                    onDelete={onDelete ? handleDelete : undefined}
                    selectable={selectable}
                    highlighted={record.id === highlightedId}
                    isDeleting={deletingId === record.id}
                    currency={currency}
                    // Fix #7/#8: pass pre-resolved translated string from the map.
                    // Fix #9: use ?? for the fallback.
                    odometerUnit={odometerUnitMap.get(record.carId ?? "") ?? t(`common:${distanceLabelShort}`)}
                    volumeLabel={t(`common:${volumeLabelShort}`)}
                    distanceLabel={t(`common:${distanceLabelShort}`)}
                    consumptionLabel={t(`common:${consumptionLabelShort}`)}
                    hasOdometerAnomaly={odometerAnomalies.has(record.id ?? "")}
                    getExpenseKindName={getExpenseKindNameById}
                    forceClose={shouldForceClose(record.id ?? "")}
                    onForceCloseComplete={() =>
                      handleForceCloseComplete(record.id ?? "")
                    }
                    onSwipeStateChange={(isOpen) =>
                      handleSwipeStateChange(record.id ?? "", isOpen)
                    }
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>

          <LoadMoreButton
            hasMore={hasMore}
            loading={loading}
            loadingMore={loadingMore}
            onLoadMore={onLoadMore}
          />
        </>
      )}
    </Stack>
  );
};

export const LogbookList = React.memo(LogbookListComponent);